<template>
    <div class="content">
        <div class="container-fluid">
            <!-- Titre & breadcrumb -->
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ $t('componentEditExtension.title') }}</h4>
                </div>
                <div class="col-auto order-2 d-none d-sm-block">
                    <ol class="breadcrumb m-0 py-2">
                        <li class="breadcrumb-item">
                            <router-link to="/installation-list">{{ $t('common.installerSpace') }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link :to="pathToInstallationEditComponents">{{ $t('componentEditExtension.breadcrumb') }}</router-link>
                        </li>
                        <li class="breadcrumb-item active">{{ $t('componentEditExtension.title') }}</li>
                    </ol>
                </div>
            </div>

            <!-- Formulaire -->
            <form id="componentForm" name="componentForm" @submit.prevent="handleSubmit" class="pb-4">
                <div class="row">
                    <div class="col-xxl-8 col-xl-7 d-flex flex-column">
                        <div class="card flex-grow-1 mb-3">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('common.identification') }}</h4>
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <!-- Colonne gauche : Image -->
                                    <div class="col-md-6 d-flex flex-column justify-content-center align-items-center text-center mb-2 mb-md-0">
                                        <img src="assets/images/relays.png" alt="Image du module Relais"
                                            class="img-fluid rounded shadow-sm mb-2"
                                            style="max-height: 200px; object-fit: contain;" />
                                    </div>

                                    <!-- Colonne droite : Infos -->
                                    <div class="col-md-6">
                                        <div class="row">
                                            <div class="mb-2 col-12">
                                                <label for="nom" class="form-label">{{ $t('componentEditExtension.hubName') }}</label>
                                                <input type="text" class="form-control" id="nom" required maxlength="32" v-model="module.label" />
                                            </div>

                                            <div class="mb-2 col-12">
                                                <label for="numero-serie" class="form-label">{{ $t('common.serialNumber') }}</label>
                                                <input type="text" class="form-control" id="numero-serie" v-model="module.getSerialNumber" readonly />
                                            </div>

                                            <div class="mb-2 col-12">
                                                <label for="versions" class="form-label">{{ $t('common.version') }}</label>
                                                <input type="text" class="form-control" id="versions" v-model="module.getVersions" readonly />
                                            </div>

                                            <div class="mb-2 col-12">
                                                <label for="warrantyStartDate" class="form-label">{{ $t('common.warrantyStart') }}</label>
                                                <input type="text" class="form-control" id="warranty-start-date" v-model="module.getWarrantyStartDate" readonly />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-xxl-4 col-xl-5">
                        <div class="card card-h-100">
                            <div class="card-header pb-0 d-flex align-items-center justify-content-between">
                                <h4 class="header-title">{{ $t('componentEditExtension.composition') }}</h4>
                                <span v-if="module.module_state === 1" class="badge bg-success font-14 px-2">{{ $t('common.connected') }}</span>
                                <span v-else class="badge bg-danger font-14 px-2">{{ $t('common.disconnected') }}</span>
                            </div>
                            <div class="card-body ps-1">
                                <div ref="tree" class="component-treeview tree-components-lite">
                                    <ul>
                                        <li data-jstree='{ "icon" : "mdi mdi-server-network text-primary", "opened" : true }'>
                                            <div class="tree-row">
                                                <div class="tree-cell component-name fw-bold text-primary">{{ hub.label }}</div>
                                                <div class="tree-cell component-action">
                                                    <TreeButton :path="hub.path()" />
                                                </div>
                                            </div>
                                            <ul>
                                                <li v-for="reader in hub.readers" :key="reader.key(hub.id)" data-jstree='{ "icon" : "mdi mdi-key-wireless text-success" }'>
                                                    <div class="tree-row">
                                                        <div class="tree-cell component-name fw-bold text-dark">{{ reader.label }}</div>
                                                        <div class="tree-cell component-type">{{ reader.typeLabel }}</div>
                                                        <div class="tree-cell component-action">
                                                            <TreeButton :path="reader.path(facilityId, hub.id)" />
                                                        </div>
                                                    </div>
                                                </li>
                                                <li v-for="module in hub.extensions" :key="module.key(hub.id)" :data-jstree="getModuleIcon(module.id)">
                                                    <div :class="['tree-row', { active: isSelected(module.id) }]">
                                                        <div class="tree-cell component-name fw-bold text-dark">{{ module.label }}</div>
                                                        <div class="tree-cell component-type">{{ $t('common.relayModule') }}</div>
                                                        <div class="tree-cell component-action">
                                                            <TreeButton v-if="!isSelected(module.id)" :path="module.path(facilityId, hub.id)" />
                                                        </div>
                                                    </div>
                                                </li>
                                            </ul>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Form actions -->
                <div class="form-actions-sticky d-flex justify-content-center align-items-center">
                    <router-link :to="pathToInstallationEditComponents" class="btn btn-outline-secondary me-2">{{ $t('common.back') }}</router-link>
                    <button type="button" class="btn btn-danger me-2" @click="replaceOrDeleteModal.show()">{{ $t('componentEditHub.replaceOrDelete') }}</button>
                    <button type="submit" class="btn btn-primary">{{ $t('common.save') }}</button>
                </div>
            </form>
        </div>
        <ReplaceOrDeleteHubModal ref="replaceOrDeleteModal" :component-type="$t('common.relayModule')" :action="handleReplaceOrDelete" />
    </div>
</template>

<script>
import { computed, reactive, ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { useI18n } from 'vue-i18n';
import { showToast } from '@/services/toast'
import TreeButton from '@/components/treeButton';
import ReplaceOrDeleteHubModal from '@/modals/replaceOrDeleteHubModal';
import $ from 'jquery';

export default {
    name: 'ComponentEditExtension',
    components: {
        TreeButton,
        ReplaceOrDeleteHubModal,
    },
    setup() {
        const userStore = useUserStore();
        const router = useRouter();
        const route = useRoute();
        const { t } = useI18n();

        const facilityId = parseInt(route.query.facilityId);
        const hubId = parseInt(route.query.hubId);
        const id = parseInt(route.query.id);

        const storeHub = userStore.getHub(facilityId, hubId);
        const hub = storeHub.clone();
        const storeModule = hub.extensions.find(module => module.id === id);
        const module = reactive(storeModule);

        const handleSubmit = async () => {
            try {
                await userStore.updateHub(facilityId, storeHub, hub);
                storeHub.update(hub);
                showToast('success', t('common.updateSuccess'));
            } catch (error) {
                showToast('error', error);
            }
        };

        const pathToInstallationEditComponents = computed(() => {
            return `/installation-edit?id=${facilityId}&action=components`;
        });

        const replaceOrDeleteModal = ref(null);

        const handleReplaceOrDelete = async (reason) => {
            try {
                await userStore.deleteExtension(facilityId, storeHub, module, reason);
                await router.push(pathToInstallationEditComponents.value);
                showToast('success', t('componentEditHub.deleteSuccess', { type: t('common.relayModule') }));
            } catch (error) {
                showToast('error', error);
            }
        };

        const isSelected = (module_id) => {
            return id === module_id ? true : undefined;
        };

        const getModuleIcon = (module_id) => {
            return JSON.stringify({
                icon: 'mdi mdi-key-wireless text-success',
                selected: isSelected(module_id)
            });
        };

        const tree = ref(null);

        onMounted(() => {
            $(tree.value).jstree();

            $(tree.value).on('click', '.tree-button', (event) => {
                const jsonURI = $(event.currentTarget).attr('data-url');
                const uri = JSON.parse(jsonURI);
                router.push({ path: uri.path, query: uri.params });
            });
        });

        return {
            tree,
            facilityId,
            hub,
            module,
            pathToInstallationEditComponents,
            handleSubmit,
            isSelected,
            getModuleIcon,
            replaceOrDeleteModal,
            handleReplaceOrDelete,
        };
    },
};
</script>
